class UrlUtils {
    /**
     * Adds a query parameter to the current URL and updates the browser's history state.
     *
     * @param {string} key - The name of the query parameter to add.
     * @param {string} value - The value of the query parameter to add.
     */
    static addQueryParam(key, value) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set(key, value);
        const url = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState({}, '', url);
    }

    /**
     * Appends a query parameter to the current URL without reloading the page.
     *
     * @param {string} key - The name of the query parameter to append.
     * @param {string} value - The value of the query parameter to append.
     */
    static appendQueryParam(key, value) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.append(key, value);
        const url = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState({}, '', url);
    }

    /**
     * Removes a query parameter from the current URL.
     *
     * @param {string} key - The key of the query parameter to remove.
     */
    static removeQueryParam(key) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete(key);
        window.history.replaceState({}, '', url);
    }
    /**
     * Retrieves the query parameters from the current URL.
     *
     * @returns {URLSearchParams} An instance of URLSearchParams containing the query parameters.
     */
    static getQueryParams() {
        return new URLSearchParams(window.location.search);
    }
    /**
     * Retrieves the value of a specified query parameter from the current URL.
     *
     * @param {string} key - The name of the query parameter to retrieve.
     * @returns {string|null} The value of the query parameter, or null if the parameter is not present.
     */
    static getQueryParam(key) {
        return new URLSearchParams(window.location.search).get(key);
    }
}
class CookieUtils {
    /**
     * Retrieves the value of a specified cookie by its name.
     *
     * @param {string} name - The name of the cookie to retrieve.
     * @returns {string|undefined} The value of the cookie if found, otherwise undefined.
     */
    static getCookie(name) {
        const value = "; " + document.cookie;
        const parts = value.split("; " + name + "=");
        if (parts.length == 2) return parts.pop().split(";").shift();
    }
    /**
     * Sets a cookie with the specified name, value, and expiration days.
     *
     * @param {string} name - The name of the cookie.
     * @param {string} value - The value of the cookie.
     * @param {number} days - The number of days until the cookie expires.
     * @param {string} [domain=null] - The domain for which the cookie is valid. Defaults to null.
     */
    static setCookie(name, value, days, domain = null) {
        let expires = "";
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = "; expires=" + date.toUTCString();
        }
        if (domain) {
            document.cookie = name + "=" + value + expires + "; domain=" + domain + "; path=/";
        }
        else {
            document.cookie = name + "=" + value + expires + "; path=/";
        }
    }
    /**
     * Deletes a cookie with the specified name.
     *
     * @param {string} name - The name of the cookie to delete.
     */
    static deleteCookie(name) {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
}
class LocalStorageUtils {
    /**
     * Stores a value in the local storage.
     *
     * @param {string} key - The key under which the value is stored.
     * @param {string} value - The value to be stored.
     */
    static setItem(key, value) {
        localStorage.setItem(key, value);
    }
    /**
     * Retrieves an item from local storage.
     *
     * @param {string} key - The key of the item to retrieve.
     * @returns {string | null} The value of the item, or null if the item does not exist.
     */
    static getItem(key) {
        return localStorage.getItem(key);
    }
}
(() => {
    if (window.poAlgoliaInsights) {
        return;
    }

    const DEFAULT_PRODUCTS_INDEX = 'shopify_products';
    const USER_TOKEN_STORAGE_KEY = 'po_algolia_user_token';
    const SOURCE_EVENT_NAMES = {
        complementary: {
            view: 'Frequently Bought Together Viewed',
            click: 'Frequently Bought Together Clicked',
            addToCart: 'Frequently Bought Together Added To Cart',
        },
        related: {
            view: 'Related Items Viewed',
            click: 'Related Items Clicked',
            addToCart: 'Related Items Added To Cart',
        },
        'cart-accessories': {
            addToCart: 'Cart Accessories Added To Cart',
        },
    };
    let memoryUserToken = '';

    const getAlgoliaConfig = () => window.algoliaShopify?.config || {};
    const safeGetLocalStorageItem = (key) => {
        try {
            return window.localStorage?.getItem(key) || '';
        } catch (_error) {
            return '';
        }
    };
    const safeSetLocalStorageItem = (key, value) => {
        try {
            window.localStorage?.setItem(key, value);
        } catch (_error) {
            // no-op: localStorage can be unavailable in privacy mode
        }
    };
    const toNumber = (value) => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const normalizedValue =
            typeof value === 'string'
                ? value.replace(',', '.').trim()
                : value;
        const parsedValue = Number(normalizedValue);
        return Number.isFinite(parsedValue) ? parsedValue : null;
    };
    const toMinorUnitPrice = (value) => {
        const numericValue = toNumber(value);
        if (numericValue === null) {
            return null;
        }

        return Number((numericValue / 100).toFixed(2));
    };
    const parseTrackingProduct = (rawValue) => {
        if (!rawValue) return null;

        if (typeof rawValue === 'object') {
            return rawValue;
        }

        if (typeof rawValue !== 'string') {
            return null;
        }

        try {
            return JSON.parse(rawValue);
        } catch (_error) {
            return null;
        }
    };
    const getDefaultIndexName = () => {
        const lastIndexName = window.poAlgoliaUtils?.getLastIndexName?.();
        if (lastIndexName) {
            return String(lastIndexName).trim();
        }

        const defaultIndexName = window.poAlgoliaUtils?.getDefaultProductsIndex?.();
        if (defaultIndexName) {
            return String(defaultIndexName).trim();
        }

        const indexPrefix = getAlgoliaConfig()?.index_prefix;
        return indexPrefix ? `${indexPrefix}products` : DEFAULT_PRODUCTS_INDEX;
    };
    const getCurrency = () => {
        const candidates = [
            window.Shopify?.currency?.active,
            window.Shopify?.currency?.currentCurrency,
            window.poShopifyLocalization?.market?.currency?.isoCode,
            window.poShopifyLocalization?.market?.currency?.iso_code,
            window.algoliaShopify?.shopify?.currency,
            window.algoliaShopify?.shopify?.market?.currency,
            window.algoliaShopify?.shopify?.market?.currency_code,
            window.algoliaShopify?.shopify?.market?.currencyCode,
        ];

        for (const candidate of candidates) {
            const normalizedCandidate = String(candidate || '').trim().toUpperCase();
            if (/^[A-Z]{3}$/.test(normalizedCandidate)) {
                return normalizedCandidate;
            }
        }

        return '';
    };
    const getEventName = (action, source = '') => {
        const normalizedSource = String(source || '').trim();
        if (SOURCE_EVENT_NAMES[normalizedSource]?.[action]) {
            return SOURCE_EVENT_NAMES[normalizedSource][action];
        }

        if (action === 'view') return 'Recommendation Viewed';
        if (action === 'click') return 'Recommendation Clicked';
        return 'Product Added To Cart';
    };
    const getTrackingPriceFromDataset = (dataset = {}) => {
        if (!dataset || typeof dataset !== 'object') {
            return null;
        }

        const trackingProduct = parseTrackingProduct(dataset.trackingProduct);
        const trackingPrice =
            toNumber(trackingProduct?.Price)
            ?? toNumber(trackingProduct?.PriceNet);
        if (trackingPrice !== null) {
            return trackingPrice;
        }

        const algoliaPrice = toNumber(dataset.algoliaPrice);
        if (algoliaPrice !== null) {
            return algoliaPrice;
        }

        return toMinorUnitPrice(dataset.laDnProductPrice);
    };
    const normalizeTrackingItem = (item = {}) => {
        const quantity = Math.max(1, parseInt(item.quantity || '1', 10) || 1);
        const objectID = String(
            item.objectID
            || item.objectId
            || item.variantId
            || item.productVariantId
            || ''
        ).trim();
        const queryID = String(item.queryID || item.queryId || '').trim();
        const indexName = String(item.indexName || item.index || getDefaultIndexName()).trim();
        const handle = String(item.handle || '').trim();
        const productId = String(item.productId || '').trim();
        const source = String(item.source || '').trim();
        const url = String(item.url || '').trim();
        const position = Math.max(1, parseInt(item.position || '0', 10) || 0) || '';
        const price = toNumber(item.price);
        const value = toNumber(item.value);
        const currency = String(item.currency || getCurrency()).trim().toUpperCase();

        return {
            objectID,
            queryID,
            indexName,
            handle,
            productId,
            source,
            url,
            position,
            quantity,
            price,
            value,
            currency,
        };
    };
    const getItemFromElement = (element, fallbackItem = {}) => {
        const aggregatedItem = {};
        let currentElement = element instanceof Element ? element : null;

        while (currentElement) {
            const { dataset } = currentElement;

            if (dataset) {
                if (!aggregatedItem.objectID && dataset.algoliaObjectId) {
                    aggregatedItem.objectID = dataset.algoliaObjectId;
                }
                if (!aggregatedItem.queryID && dataset.algoliaQueryId) {
                    aggregatedItem.queryID = dataset.algoliaQueryId;
                }
                if (!aggregatedItem.indexName && dataset.algoliaIndexName) {
                    aggregatedItem.indexName = dataset.algoliaIndexName;
                }
                if (!aggregatedItem.position && dataset.algoliaPosition) {
                    aggregatedItem.position = dataset.algoliaPosition;
                }
                if (!aggregatedItem.source && dataset.algoliaSource) {
                    aggregatedItem.source = dataset.algoliaSource;
                }
                if (!aggregatedItem.handle && dataset.laDnProductHandle) {
                    aggregatedItem.handle = dataset.laDnProductHandle;
                }
                if (!aggregatedItem.productId && dataset.laDnProductId) {
                    aggregatedItem.productId = dataset.laDnProductId;
                }
                if (aggregatedItem.price === undefined) {
                    const trackingPrice = getTrackingPriceFromDataset(dataset);
                    if (trackingPrice !== null) {
                        aggregatedItem.price = trackingPrice;
                    }
                }
            }

            currentElement = currentElement.parentElement;
        }

        return normalizeTrackingItem({
            ...fallbackItem,
            ...aggregatedItem,
        });
    };
    const annotateElement = (element, item = {}) => {
        if (!(element instanceof Element)) {
            return element;
        }

        const normalizedItem = normalizeTrackingItem(item);

        if (normalizedItem.objectID) {
            element.dataset.algoliaObjectId = normalizedItem.objectID;
        }
        if (normalizedItem.queryID) {
            element.dataset.algoliaQueryId = normalizedItem.queryID;
        }
        if (normalizedItem.indexName) {
            element.dataset.algoliaIndexName = normalizedItem.indexName;
        }
        if (normalizedItem.position) {
            element.dataset.algoliaPosition = String(normalizedItem.position);
        }
        if (normalizedItem.source) {
            element.dataset.algoliaSource = normalizedItem.source;
        }
        if (normalizedItem.price !== null) {
            element.dataset.algoliaPrice = String(normalizedItem.price);
        }

        return element;
    };
    const buildAnonymousUserToken = () => {
        if (window.crypto?.randomUUID) {
            return `anonymous-${window.crypto.randomUUID()}`;
        }

        return `anonymous-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    };
    const getUserToken = () => {
        if (memoryUserToken) {
            return memoryUserToken;
        }

        const storedUserToken = String(safeGetLocalStorageItem(USER_TOKEN_STORAGE_KEY) || '').trim();
        if (storedUserToken) {
            memoryUserToken = storedUserToken;
            return storedUserToken;
        }

        memoryUserToken = buildAnonymousUserToken();
        safeSetLocalStorageItem(USER_TOKEN_STORAGE_KEY, memoryUserToken);
        return memoryUserToken;
    };
    const sendEvents = async (events = []) => {
        const algoliaConfig = getAlgoliaConfig();
        const validEvents = Array.isArray(events)
            ? events.filter((event) => {
                return (
                    event
                    && typeof event === 'object'
                    && String(event.eventName || '').trim()
                    && String(event.index || '').trim()
                    && Array.isArray(event.objectIDs)
                    && event.objectIDs.length
                );
            })
            : [];

        if (!algoliaConfig?.app_id || !algoliaConfig?.search_api_key || !validEvents.length) {
            return false;
        }

        try {
            const response = await fetch('https://insights.algolia.io/1/events', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'x-algolia-application-id': algoliaConfig.app_id,
                    'x-algolia-api-key': algoliaConfig.search_api_key,
                },
                body: JSON.stringify({
                    events: validEvents.map((event) => ({
                        userToken: getUserToken(),
                        ...event,
                    })),
                }),
                keepalive: true,
            });

            if (!response.ok) {
                throw new Error(`Algolia Insights HTTP ${response.status}`);
            }

            return true;
        } catch (error) {
            console.error('[po-algolia-insights] sendEvents error', error);
            return false;
        }
    };
    const buildViewedEvents = (items = [], source = '', eventName = '') => {
        const eventsByIndex = new Map();

        items
            .map((item) => normalizeTrackingItem(item))
            .filter((item) => item.objectID && item.indexName)
            .forEach((item) => {
                const eventKey = item.indexName;
                const currentGroup = eventsByIndex.get(eventKey) || [];
                currentGroup.push(item.objectID);
                eventsByIndex.set(eventKey, currentGroup);
            });

        return Array.from(eventsByIndex.entries()).flatMap(([index, objectIDs]) => {
            const dedupedObjectIds = [...new Set(objectIDs)];
            const chunks = [];

            for (let i = 0; i < dedupedObjectIds.length; i += 20) {
                chunks.push({
                    eventType: 'view',
                    eventName: eventName || getEventName('view', source),
                    index,
                    objectIDs: dedupedObjectIds.slice(i, i + 20),
                });
            }

            return chunks;
        });
    };
    const buildClickedEvent = (item = {}, source = '', eventName = '') => {
        const normalizedItem = normalizeTrackingItem(item);

        if (!normalizedItem.objectID || !normalizedItem.indexName) {
            return null;
        }

        const nextEvent = {
            eventType: 'click',
            eventName: eventName || getEventName('click', normalizedItem.source || source),
            index: normalizedItem.indexName,
            objectIDs: [normalizedItem.objectID],
        };

        if (normalizedItem.queryID) {
            nextEvent.queryID = normalizedItem.queryID;
            if (normalizedItem.position) {
                nextEvent.positions = [normalizedItem.position];
            }
        }

        return nextEvent;
    };
    const buildAddedToCartEvent = (item = {}, source = '', eventName = '') => {
        const normalizedItem = normalizeTrackingItem(item);

        if (!normalizedItem.objectID || !normalizedItem.indexName) {
            return null;
        }

        const nextEvent = {
            eventType: 'conversion',
            eventSubtype: 'addToCart',
            eventName: eventName || getEventName('addToCart', normalizedItem.source || source),
            index: normalizedItem.indexName,
            objectIDs: [normalizedItem.objectID],
        };
        const objectData = {};

        if (normalizedItem.queryID) {
            nextEvent.queryID = normalizedItem.queryID;
            objectData.queryID = normalizedItem.queryID;
        } else {
            nextEvent.inferQueryID = true;
        }

        if (normalizedItem.quantity) {
            objectData.quantity = normalizedItem.quantity;
        }
        if (normalizedItem.price !== null) {
            objectData.price = normalizedItem.price;
        }

        if (Object.keys(objectData).length) {
            nextEvent.objectData = [objectData];
        }

        const finalValue =
            normalizedItem.value !== null
                ? normalizedItem.value
                : (normalizedItem.price !== null
                    ? Number((normalizedItem.price * normalizedItem.quantity).toFixed(2))
                    : null);

        if (normalizedItem.currency && (finalValue !== null || normalizedItem.price !== null)) {
            nextEvent.currency = normalizedItem.currency;
        }
        if (finalValue !== null && normalizedItem.currency) {
            nextEvent.value = finalValue;
        }

        return nextEvent;
    };

    window.poAlgoliaInsights = {
        annotateElement,
        buildAddedToCartEvent,
        buildClickedEvent,
        buildViewedEvents,
        getEventName,
        getItemFromElement,
        getUserToken,
        normalizeTrackingItem,
        sendEvents,
        trackAddedToCart({ items = [], item = null, source = '', eventName = '' } = {}) {
            const normalizedItems =
                item !== null
                    ? [item]
                    : (Array.isArray(items) ? items : [items]);
            const events = normalizedItems
                .map((entry) => buildAddedToCartEvent(entry, source, eventName))
                .filter(Boolean);

            if (!events.length) {
                return Promise.resolve(false);
            }

            return sendEvents(events);
        },
        trackClickedItem({ item = {}, source = '', eventName = '' } = {}) {
            const event = buildClickedEvent(item, source, eventName);
            if (!event) {
                return Promise.resolve(false);
            }

            return sendEvents([event]);
        },
        trackViewedItems({ items = [], source = '', eventName = '' } = {}) {
            const events = buildViewedEvents(
                Array.isArray(items) ? items : [items],
                source,
                eventName
            );

            if (!events.length) {
                return Promise.resolve(false);
            }

            return sendEvents(events);
        },
    };
})();
class DateUtils {
    /**
     * Gets the current date formatted as 'YYYY-MM-DD' in the specified timezone.
     *
     * @param {string} timezone - The IANA timezone identifier (e.g., 'America/New_York').
     * @returns {string} The current date in the format 'YYYY-MM-DD'.
     */
    static getCurrentDate(timezone) {
        const date = new Date();

        const year = date.toLocaleString('default', {
            timeZone: timezone,
            year: 'numeric'
        });
        const month = date.toLocaleString('default', {
            timeZone: timezone,
            month: '2-digit',
        });
        const day = date.toLocaleString('default', {
            timeZone: timezone,
            day: '2-digit'
        });
        return [year, month, day].join('-');
    }
    /**
     * Checks if the current date is between the given start and end dates.
     *
     * @param {Date} startDate - The start date to compare.
     * @param {Date} endDate - The end date to compare.
     * @returns {boolean} - Returns true if the current date is between the start and end dates, otherwise false.
     */
    static checkDateBetween(timezone, startDate, endDate) {
        if (!startDate || !endDate) return true;
        const date = this.getCurrentDate(timezone);
        return date >= startDate && date <= endDate;
    }

    /**
     * Gets the current date time formatted as 'YYYY-MM-DD hh:mm:ss' in the specified timezone.
     * 
     * @param {string} timezone - The IANA timezone identifier (e.g., 'America/New_York'). 
     * @returns {string} - The current date time in the format 'YYYY-MM-DD hh:mm:ss'.
     */
    static getCurrentDateTime(timezone) {
        const date = new Date();
        let year = date.toLocaleString('default', {
            timeZone: timezone,
            year: 'numeric'
        });
        let month = date.toLocaleString('default', {
            timeZone: timezone,
            month: '2-digit',
        });
        let day = date.toLocaleString('default', {
            timeZone: timezone,
            day: '2-digit'
        });

        let hour = date.toLocaleTimeString('default', {
            timeZone: timezone,
            hour: '2-digit',
            hour12: false
        });

        let minute = date.toLocaleTimeString('default', {
            timeZone: timezone,
            minute: '2-digit'
        });
        let second = date.toLocaleTimeString('default', {
            timeZone: timezone,
            second: '2-digit'
        });

        if (minute.length == 1) {
            minute = '0' + minute;
        }
        if (second.length == 1) {
            second = '0' + second;
        }

        return [year, month, day].join('-') + ' ' + [hour, minute, second].join(':');
    }


    /**
     * Returns an array containing the values to be shown in the countdown
     * 
     * @param {Date} countDownDate - The date that i want to set the countdown for
     * @param {string} timezone - The IANA timezone identifier (e.g., 'America/New_York'). 
     * @returns {Array} - Returns an array of three elements like this ["24h","59m","59s"]
     */
    static getCountdown(countDownDate, timezone) {
        const currentDateTime = this.getCurrentDateTime(timezone);
        const currentTimestamp = new Date(currentDateTime.replace(' ', 'T')).getTime();
        const distance = countDownDate - currentTimestamp;

        if (distance < 0) {
            return [];
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        let countdownValues = [];

        if (days > 0) {
            countdownValues.push(days + "d");
            countdownValues.push(hours + "h");
            countdownValues.push(minutes + "m");
            countdownValues.push(seconds + "s");
        } else {
            if (hours > 0) {
                countdownValues.push(hours + "h");
                countdownValues.push(minutes + "m");
                countdownValues.push(seconds + "s");
            } else {
                if (minutes > 0) {
                    countdownValues.push(minutes + "m");
                    countdownValues.push(seconds + "s");
                } else {
                    countdownValues.push(seconds + "s");
                }
            }
        }

        return countdownValues;
    }
}
class Fetchutils {
    /**
     * Fetches data from a specified URL and returns the response as JSON.
     *
     * @param {string} url - The URL to fetch the data from.
     * @returns {Promise<any>} A promise that resolves with the JSON response data.
     */
    static async fetchJson(url) {
        const response = await fetch(url);
        return response.json();
    }
    /**
     * Fetches HTML content from a given URL and parses it into a Document object.
     *
     * @param {string} url - The URL to fetch the HTML content from.
     * @returns {Promise<Document>} A promise that resolves to a Document object containing the parsed HTML.
     */
    static async fetchHtml(url) {
        const response = await fetch(url);
        const text = await response.text();
        return new DOMParser().parseFromString(text, 'text/html');
    }
    /**
     * Sends a POST request with JSON data to the specified URL and returns the JSON response.
     *
     * @param {string} url - The URL to send the POST request to.
     * @param {Object} data - The data to be sent in the body of the POST request.
     * @returns {Promise<Object>} - A promise that resolves to the JSON response.
     */
    static async postJson(url, data) {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        return response.json();
    }


    /**
     * Sends a POST request to a GraphQL endpoint.
     *
     * @param {string} url - The URL of the GraphQL endpoint.
     * @param {string} token - The Shopify Storefront Access Token.
     * @param {string} query - The GraphQL query to be sent.
     * @returns {Promise<Object>} - A promise that resolves to the JSON response from the server.
     */
    static async postGraphql(url, token, query) {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                //'Content-Type': 'application/graphql',
                'X-Shopify-Storefront-Access-Token': token
            },
            body: JSON.stringify({ query })
        });
        return response.json();
    }
}
class ObserverUtils {
    /**
     * Creates a new IntersectionObserver instance.
     *
     * @param {IntersectionObserverCallback} callback - The function to be called when the observed elements intersect with the viewport.
     * @param {IntersectionObserverInit} options - Optional configuration options for the IntersectionObserver.
     * @returns {IntersectionObserver} A new IntersectionObserver instance.
     */
    static createIntersectionObserver(callback, options) {
        return new IntersectionObserver(callback, options);
    }
    /**
     * Creates a new MutationObserver instance.
     *
     * @param {MutationCallback} callback - The function to be called when mutations are observed.
     * @param {MutationObserverInit} options - Options for configuring the observer.
     * @returns {MutationObserver} A new MutationObserver instance.
     */
    static createMutationObserver(callback, options) {
        return new MutationObserver(callback, options);
    }

    /**
     * Waits for an element to be added to the DOM.
     *
     * @param {string} selector - The selector of the element to wait for.
     * @param {Element} container - The container element to search within, by default it's document.body.
     * @param {number} timeout - Optional timeout in milliseconds. If provided, the promise will reject if the element is not found within this time.
     * @returns {Promise<Element>} A promise that resolves with the found element or rejects if the element is not found within the timeout (if declared).
     */
    static waitForElement(selector, container = document.body, timeout) {
        return new Promise((resolve, reject) => {
            const observer = new MutationObserver((mutationsList) => {
                for (const mutation of mutationsList) {
                    if (mutation.type === 'childList') {
                        if (container.querySelector(selector)) {
                            resolve(document.querySelector(selector));
                            observer.disconnect();
                            return;
                        }
                    }
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });

            if (!timeout) return;
            setTimeout(() => {
                reject(new Error(`Timeout waiting for element: ${selector}`));
            }, timeout);
        });
    }
}
class PubSub {
    static subscribers = {};

    /**
     * Publishes an event to all subscribers.
     *
     * @param {string} eventName - The name of the event to publish.
     * @param {*} data - The data to pass to the event subscribers.
     */
    static publish(eventName, data) {
        if (subscribers[eventName]) {
            subscribers[eventName].forEach((callback) => {
                callback(data);
            });
        }
    }
    /**
     * Subscribes a callback function to a specific event.
     * 
     * @param {string} eventName - The name of the event to subscribe to.
     * @param {Function} callback - The callback function to be executed when the event is triggered.
     * @returns {Function} A function to unsubscribe the callback from the event.
     */
    static subscribe(eventName, callback) {
        if (subscribers[eventName] === undefined) {
            subscribers[eventName] = [];
        }
        subscribers[eventName] = [...subscribers[eventName], callback];
        return function unsubscribe() {
            subscribers[eventName] = subscribers[eventName].filter((cb) => {
                return cb !== callback;
            });
        };
    }
}

class PoAlgoliaUtilities extends HTMLElement {
    constructor() {

        super();

        this.algoliaConfig = window.algoliaShopify?.config;

        this.algoliaApiKey = this.algoliaConfig?.search_api_key;
        this.algoliaAppId = this.algoliaConfig?.app_id;
        this.marketId = window.poShopifyLocalization?.market?.id || window.algoliaShopify?.shopify?.market?.id;
        this.currentMarket =
            window.poAlgoliaUtils?.getCurrentMarket?.()
            || this.algoliaConfig?.markets_mapping?.find(market => String(market?.id) === String(this.marketId));
        const normalizedSuffix =
            window.poAlgoliaUtils?.normalizeMarketSuffix?.(this.currentMarket?.suffix) || this.currentMarket?.suffix;
        
        this.productsIndex = this.algoliaConfig?.index_prefix
            ? `${this.algoliaConfig.index_prefix}products${normalizedSuffix ? `_${normalizedSuffix}` : ""}`
            : null;

        this.querySuggestionsIndex = this.algoliaConfig?.query_suggestions_index_name;
        this.apiUrl = this.algoliaAppId ? `https://${this.algoliaAppId}-dsn.algolia.net` : null;

        if (!this.apiUrl) {
            console.warn("Unable to retrieve Algolia API URL. Configuration:", this.algoliaConfig);
        }
    }

    fetchRelatedProducts(indexName, objectIDs, maxRecommendations, showOnlyAvailable, model = "related-products", threshold = 0) {
        const url = this.apiUrl ? new URL(`${this.apiUrl}/1/indexes/*/recommendations`) : null;
        const requests = [];
        const facetFilters = [];
        indexName = indexName || this.productsIndex;
        const userToken = window.poAlgoliaInsights?.getUserToken?.();

        if (!url) {
            console.error("Algolia API URL is not set. Configuration:", this.algoliaConfig);
            return;
        }

        // objectID is the product ID taken from the hit object
        if (!objectIDs) {
            console.error("objectID is required");
            return;
        } 
        
        if (!indexName) {
            console.error("indexName is required");
            return;
        } 
        
        if (!model) {
            console.error("model is required");
            return;
        }
        
        url.searchParams.set("x-algolia-api-key", this.algoliaApiKey);
        url.searchParams.set("x-algolia-application-id", this.algoliaAppId);

        if (showOnlyAvailable) {
            facetFilters.push("inventory_available:true");
        }

        if (Array.isArray(objectIDs)) {
            objectIDs.forEach(id => requests.push({
                indexName,
                model,
                queryParameters: {
                    facetFilters,
                    clickAnalytics: true,
                    ...(userToken ? { userToken } : {}),
                },
                objectID: id,
                threshold
            }));
        } else {
            requests.push({
                indexName,
                model,
                queryParameters: {
                    facetFilters,
                    clickAnalytics: true,
                    ...(userToken ? { userToken } : {}),
                },
                objectID: objectIDs,
                threshold
            });
        }

        if (maxRecommendations) {
            requests.forEach(req => req.maxRecommendations = maxRecommendations);
        }

        return fetch(url, {
            body: JSON.stringify({ requests }),
            method: "POST",
        })
        .then(response => response.json())
        .then(data => {
            return data;
        })
        .catch(error => {
            console.error('Error:', error);
            throw error;
        });
    }

    fetchPopularSearches(indexName = this.productsIndex) {
        if (!indexName) {
            console.error("indexName is required");
            return;
        }
        
        const url = `${this.apiUrl}/1/analytics/searches`;
        const params = new URLSearchParams({
            "x-algolia-api-key": this.algoliaApiKey,
            "x-algolia-application-id": this.algoliaAppId
        });

        // const body = JSON.stringify({
        //     requests: [
        //         {
        //             indexName: indexName
        //         }
        //     ]
        // });

        return fetch(`${url}?${params.toString()}`, {
            // body: body,
            method: "GET",
        })
        .then(response => response.json())
        .then(data => {
            return data;
        })
        .catch(error => {
            console.error('Error:', error);
            throw error;
        });
    }

    fetchTrendingItems(indexName = this.productsIndex, options = {}) {
        const algoliaApiKey = algoliaShopify.config.search_api_key;
        const algoliaAppId = algoliaShopify.config.app_id;
        const normalizedOptions = typeof options === "string"
            ? { ruleContext: options }
            : (options || {});
        const hitsPerPage = Math.max(1, Number(normalizedOptions.hitsPerPage) || 9);
        const ruleContexts = Array.isArray(normalizedOptions.ruleContexts)
            ? normalizedOptions.ruleContexts.filter(Boolean)
            : [normalizedOptions.ruleContexts || normalizedOptions.ruleContext || "POPULAR_ITEMS"].filter(Boolean);
        if (!ruleContexts.length) {
            ruleContexts.push("POPULAR_ITEMS");
        }
        const includeUserToken = normalizedOptions.includeUserToken !== false;
        const userToken = includeUserToken ? window.poAlgoliaInsights?.getUserToken?.() : null;

        const url = `https://${algoliaAppId}-dsn.algolia.net/1/indexes/*/queries`;
        const queryParams = new URLSearchParams({
            "x-algolia-api-key": algoliaApiKey,
            "x-algolia-application-id": algoliaAppId
        });
        const requestParams = new URLSearchParams({
            hitsPerPage: String(hitsPerPage),
            clickAnalytics: "true",
            ruleContexts: JSON.stringify(
                ruleContexts.includes("POPULAR_ITEMS")
                    ? [...new Set([...ruleContexts, "upc_collapsed"])]
                    : ruleContexts
            )
        });
        if (includeUserToken && userToken) {
            requestParams.set("userToken", userToken);
        }
        const serializedRequestParams = requestParams.toString();

        const body = JSON.stringify({
            requests: [
                {
                    indexName: indexName,
                    params: serializedRequestParams
                }
            ]
        });

        return fetch(`${url}?${queryParams.toString()}`, {
            body: body,
            method: "POST",
            headers: {
                "content-type": "application/json"
            }
        })
        .then(response => {
            if (!response.ok) throw new Error(`Failed to fetch Algolia popular items. Error status: ${response.status}`);
            return response.json()
        })
    }

    async fetchProductGroup (indexName, xGroupKeys = []) {
        const url = this.apiUrl ? new URL(`${this.apiUrl}/1/indexes/*/queries`) : null;

        if (!url) {
            console.error("Algolia API URL is not set. Configuration:", this.algoliaConfig);
            return;
        }

        url.searchParams.set("x-algolia-api-key", this.algoliaApiKey);
        url.searchParams.set("x-algolia-application-id", this.algoliaAppId);

        if (!Array.isArray(xGroupKeys)) {
            xGroupKeys = [xGroupKeys];
        }

        const requests = xGroupKeys.map(key => {
            const params = new URLSearchParams({
                facetFilters: JSON.stringify([`meta.custom.x_groupkey:${key}`]),
                facets: JSON.stringify(["meta.custom.x_groupkey"]),
            });

            const req = {
                indexName: indexName || this.productsIndex,
                params: params.toString(),
            }

            return req;
        });

        const body = JSON.stringify({ requests });
        const res = await fetch(url, { body, method: "POST" });
        return await res.json();
    }
}

class GeneralUtils {
    /**
     * Stores a value in the local storage.
     * Original function taken from: https://github.com/mktj/parsecurrency
     * License: https://github.com/mktj/parsecurrency?tab=MIT-1-ov-file
     *
     * @param {string} priceStr - The price string to be parsed.
     * @returns {Object|null} An object containing parsed currency details or null if parsing fails.
     */
    static parseCurrency(priceStr) {
        const currencyMatcher = /^(?:([-+]{1}) ?)?(?:([A-Z]{3}) ?)?(?:([^\d ]+?) ?)?(?:([-+]{1}) ?)?(((?:\d{1,3}([,. ’'\u00A0\u202F]))*?\d{1,})(([,.])\d{1,2})?)(?: ?([^\d]+?))??(?: ?([A-Z]{3}))?$/;
        const gr = /^\d{1,3}([,. ’'\u00A0\u202F]\d{3})*$/; // validate groups
        const ind = /^\d{1,2}(,\d{2})*(,\d{3})?$/; // exception for Indian number format

        if (!priceStr || !priceStr.match) return null;
        priceStr = priceStr.trim();
        const match = priceStr.match(currencyMatcher);
        if (!match) return null;
        const groupSeparator = match[7] || '';
        const decimalSeparator = match[9] || '';
        if (groupSeparator === decimalSeparator && decimalSeparator) return null;
        const isNeg = match[1] === '-' || match[4] === '-';
        const integer = isNeg ? '-' + match[6] : match[6];
        if (groupSeparator && !match[6].match(gr) && !match[6].match(ind)) return null;
        let value = match[5];
        if (!value) return null;
        if (groupSeparator) value = value.replace(RegExp('\\' + groupSeparator, 'g'), '');
        if (decimalSeparator) value = value.replace(decimalSeparator, '.');
        const numericVal = isNeg ? value * -1 : +value;
        if (typeof numericVal !== 'number' || isNaN(numericVal)) return null;

        return {
            raw: priceStr,
            value: numericVal,
            integer: integer || '',
            decimals: match[8] || '',
            currency: match[2] || match[11] || '',
            symbol: match[3] || match[10] || '',
            decimalSeparator: decimalSeparator,
            groupSeparator: groupSeparator,
            sign: match[1] || match[4] || ''
        };
    };

    /**
     * Creates and returns a debounced version of the provided function that delays its execution until after
     * a specified wait time has elapsed since the last time it was invoked.
     *
     * @param {Function} func - The function to debounce.
     * @param {number} wait - The number of milliseconds to delay.
     * @returns {Function} A debounced version of the provided function.
     */
    static debounce(func, wait) {
        let timer;
        return (...args) =>
            new Promise((resolve) => {
                clearTimeout(timer);
                timer = setTimeout(() => resolve(func(...args)), wait);
            });
    }
}
